"use client"

import React, { createContext, useContext, useEffect, useRef, type ReactNode } from "react"
import { io, type Socket } from "socket.io-client"
import { SOCKET_URL } from "../config/api"
import { useAuth } from "./AuthContext"
import { useRestaurantAuth } from "./RestaurantAuthContext"

interface SocketContextType {
  socket: Socket | null
  isConnected: boolean
}

export const SocketContext = createContext<SocketContextType | undefined>(undefined)

export const SocketProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  console.log("🔌 SocketProvider rendering")
  const socketRef = useRef<Socket | null>(null)
  const { token: adminToken, isAuthenticated: isAdminAuthenticated } = useAuth()
  const { token: restaurantToken, isAuthenticated: isRestaurantAuthenticated, user: restaurantUser } = useRestaurantAuth() // Assume we add this import
  const [isConnected, setIsConnected] = React.useState(false)

  // Determine which token to use (prioritize restaurant if active)
  const token = restaurantToken || adminToken
  const isAuthenticated = isRestaurantAuthenticated || isAdminAuthenticated

  useEffect(() => {
    if (isAuthenticated && token) {
      // Initialize socket connection
      socketRef.current = io(SOCKET_URL, {
        auth: {
          token,
        },
        transports: ["polling"], // Use polling only for reliability with HTTPS
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        timeout: 15000,
      })

      const socket = socketRef.current

      socket.on("connect", () => {
        console.log("Socket connected:", socket.id)
      })

      socket.on("authenticated", (data) => {
        console.log("Socket authenticated:", data)
        setIsConnected(true)

        // If restaurant user, join restaurant room
        if (isRestaurantAuthenticated && restaurantUser?.restaurant?._id) {
          console.log("Joining restaurant room:", restaurantUser.restaurant._id)
          socket.emit("join_restaurant", { restaurantId: restaurantUser.restaurant._id })
        }
      })

      socket.on("auth_error", (error) => {
        console.error("Socket authentication error:", error)
        setIsConnected(false)
      })

      socket.on("disconnect", () => {
        console.log("Socket disconnected")
        setIsConnected(false)
      })

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error)
        setIsConnected(false)
      })

      // Cleanup on unmount or auth change
      return () => {
        if (socket) {
          socket.off("connect");
          socket.off("authenticated");
          socket.off("auth_error");
          socket.off("disconnect");
          socket.off("connect_error");
          socket.disconnect()
          socketRef.current = null
          setIsConnected(false)
        }
      }
    } else {
      // Disconnect if not authenticated
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
        setIsConnected(false)
      }
    }
  }, [isAuthenticated, token, isRestaurantAuthenticated, restaurantUser])

  const value: SocketContextType = {
    socket: socketRef.current,
    isConnected,
  }

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
}

export const useSocket = () => {
  const context = useContext(SocketContext)
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider")
  }
  return context
}
